import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FaMoneyBillWave, FaBolt, FaUser, FaPhone, FaBuilding } from 'react-icons/fa';
import SecureServiceBase from './SecureServiceBase';

const SecureFinoDMTModal = ({ show, onHide, onSuccess }) => {
  const [recipientName, setRecipientName] = useState('');
  const [recipientMobile, setRecipientMobile] = useState('');
  const [amount, setAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [transferType, setTransferType] = useState('bank');

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000];

  // Popular banks for quick selection
  const popularBanks = [
    { code: 'SBIN', name: 'State Bank of India' },
    { code: 'HDFC', name: 'HDFC Bank' },
    { code: 'ICIC', name: 'ICICI Bank' },
    { code: 'AXIS', name: 'Axis Bank' },
    { code: 'KOTK', name: 'Kotak Mahindra Bank' },
    { code: 'PNB', name: 'Punjab National Bank' }
  ];

  const handleAmountChange = (val, validateAmount) => {
    setAmount(val);
    if (validateAmount) {
      validateAmount(val);
    }
  };

  const handleBankSelect = (bank) => {
    setBankName(bank.name);
    setIfscCode(bank.code);
  };

  const handleSubmit = (e, submitHandler) => {
    e.preventDefault();
    
    if (!recipientName || !recipientMobile || !amount || !bankAccount || !ifscCode) {
      return;
    }

    const formData = {
      recipient_name: recipientName,
      recipient_mobile: recipientMobile,
      amount: Number(amount),
      bank_account: bankAccount,
      ifsc_code: ifscCode,
      bank_name: bankName,
      type: 'fino_dmt_transfer'
    };

    submitHandler(formData);
  };

  return (
    <SecureServiceBase
      show={show}
      onHide={onHide}
      onSuccess={onSuccess}
      serviceName="Fino DMT Transfer"
      serviceEndpoint="/api/fino-dmt/process"
      requiresBalance={true}
      minAmount={100}
    >
      {({ balance, error, loading, validateAmount, handleSubmit, isFormValid }) => (
        <form onSubmit={(e) => handleSubmit(e, handleSubmit)}>
          {/* Recipient Details */}
          <div className="mb-3">
            <label htmlFor="recipientName" className="form-label">
              Recipient Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="recipientName"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Enter recipient's full name"
              disabled={loading}
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="recipientMobile" className="form-label">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="tel"
              className="form-control"
              id="recipientMobile"
              value={recipientMobile}
              onChange={(e) => setRecipientMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="Enter 10-digit mobile number"
              maxLength="10"
              disabled={loading}
              required
            />
          </div>

          {/* Bank Selection */}
          <div className="mb-3">
            <label className="form-label fw-bold">Select Bank</label>
            <div className="row g-2">
              {popularBanks.map((bank) => (
                <div key={bank.code} className="col-md-6 col-sm-4">
                  <Card 
                    className={`h-100 cursor-pointer ${bankName === bank.name ? 'border-primary' : ''}`}
                    onClick={() => handleBankSelect(bank)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="text-center p-2">
                      <FaBuilding className="text-primary mb-1" />
                      <h6 className="mb-1 small">{bank.name}</h6>
                      <Badge bg="secondary" className="small">{bank.code}</Badge>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Bank Account Details */}
          <div className="mb-3">
            <label htmlFor="bankAccount" className="form-label">
              Bank Account Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="bankAccount"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter bank account number"
              disabled={loading}
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="ifscCode" className="form-label">
              IFSC Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="ifscCode"
              value={ifscCode}
              onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
              placeholder="Enter IFSC code"
              maxLength="11"
              disabled={loading}
              required
            />
          </div>

          {/* Amount Input */}
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Amount <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="number"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="amount"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value, validateAmount)}
                placeholder="Enter amount"
                min="100"
                step="1"
                disabled={loading}
                required
              />
            </div>
            {error && (
              <div className="invalid-feedback d-block">
                {error}
              </div>
            )}
            {balance !== null && amount && !error && (
              <div className="form-text text-success">
                Remaining balance: ₹{(balance - Number(amount)).toFixed(2)}
              </div>
            )}
          </div>

          {/* Quick Amount Selection */}
          <div className="mb-3">
            <label className="form-label">Quick Amounts</label>
            <div className="d-flex flex-wrap gap-2">
              {quickAmounts.map((quickAmount) => (
                <Button
                  key={quickAmount}
                  variant={amount === quickAmount.toString() ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => handleAmountChange(quickAmount.toString(), validateAmount)}
                  disabled={loading}
                >
                  ₹{quickAmount}
                </Button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-grid">
            <Button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid || !recipientName || !recipientMobile || !amount || !bankAccount || !ifscCode}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </span>
                  Processing...
                </>
              ) : (
                <>
                  <FaBolt className="me-2"></FaBolt>
                  Transfer ₹{amount || '0'}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </SecureServiceBase>
  );
};

export default SecureFinoDMTModal;
